import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { AuthOptimizations, authPerformanceOptimizer } from '../auth-performance-optimizer';
import { API_BASE_URL } from '../config';
import { getDeviceId } from '../utils/deviceId';
import { isPublicAuthRequest } from '../utils/authInterceptor';

const AUTH_API_URL = `${API_BASE_URL}/api/v1/auth`;

// Enhanced interfaces
interface LoginData {
    companyId?: string;
    username: string;
    password: string;
}

interface RegisterCompanyData {
    language?: string; // Language used during registration (e.g., 'zh' or 'en')
    companyName: string;
    companyCode: string;
    adminFirstName: string;
    adminLastName: string;
    adminEmail: string;
    adminUsername: string;
    adminPassword: string;
    companyType: string;
}

interface SetNewPasswordData {
    passwordResetToken?: string;
    username?: string;
    newPassword: string;
    confirmPassword?: string;
}

interface LoginResponse {
    success: boolean;
    token?: string;
    user?: any;
    passwordResetRequired?: boolean;
    passwordResetToken?: string;
    resetToken?: string;
    error?: string;
}

interface DecodedToken {
    userId: string;
    role: string;
    iat: number;
    exp: number;
}

// Optimized Authentication Service
export class OptimizedAuthService {
    private tokenCache: string | null = null;
    private userInfoCache: any = null;
    private lastCacheTime = 0;
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    constructor() {
        this.setupAxiosInterceptors();
        console.log('🚀 [Auth] Optimized Authentication Service initialized');
    }

    // Setup axios interceptors for automatic token handling
    private setupAxiosInterceptors() {
        // Request interceptor - add token automatically
        axios.interceptors.request.use(
            (config) => {
                const token = this.getToken();
                if (token && config.url?.includes('/api/')) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor - handle auth errors
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    const requestUrl = error.config?.url || '';
                    if (isPublicAuthRequest(requestUrl)) {
                        return Promise.reject(error);
                    }

                    // Only auto-logout if NOT during initial language fetch after login
                    // Language fetch 401 should not trigger logout (might be timing issue in Edge)
                    const isLanguageFetch = error.config?.url?.includes('/system-settings/language');
                    if (!isLanguageFetch) {
                        if (error.response?.data?.code === 'SESSION_REVOKED') {
                            try {
                                sessionStorage.setItem('logoutReason', 'SESSION_REVOKED');
                            } catch {
                                // The redirect still proceeds if session storage is unavailable.
                            }
                        }
                        console.warn('🔐 [Auth] Unauthorized response, clearing auth state');
                        this.logout();
                        window.location.href = '/login';
                    } else {
                        console.warn('🔐 [Auth] Language fetch 401, ignoring (Edge timing issue)');
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    // Fast token retrieval with caching
    getToken(): string | null {
        authPerformanceOptimizer.startTiming('getToken');
        
        // Check cache first
        if (this.tokenCache && Date.now() - this.lastCacheTime < this.CACHE_DURATION) {
            authPerformanceOptimizer.endTiming('getToken', 'cached');
            return this.tokenCache;
        }

        // Get from localStorage
        const token = localStorage.getItem('token');
        if (token) {
            this.tokenCache = token;
            this.lastCacheTime = Date.now();
        }

        authPerformanceOptimizer.endTiming('getToken', token ? 'found' : 'not-found');
        return token;
    }

    // Fast token validation using optimized decoder
    isTokenValid(): boolean {
        authPerformanceOptimizer.startTiming('isTokenValid');
        
        const token = this.getToken();
        if (!token) {
            authPerformanceOptimizer.endTiming('isTokenValid', 'no-token');
            return false;
        }

        try {
            const decoded = AuthOptimizations.fastTokenValidation(token);
            const isValid = decoded && decoded.exp * 1000 > Date.now();
            
            authPerformanceOptimizer.endTiming('isTokenValid', isValid ? 'valid' : 'expired');
            return isValid;
        } catch (error) {
            authPerformanceOptimizer.endTiming('isTokenValid', 'error');
            return false;
        }
    }

    // Fast token decoding with caching
    decodeToken(): DecodedToken | null {
        authPerformanceOptimizer.startTiming('decodeToken');
        
        const token = this.getToken();
        if (!token) {
            authPerformanceOptimizer.endTiming('decodeToken', 'no-token');
            return null;
        }

        try {
            const decoded = AuthOptimizations.fastTokenValidation(token);
            authPerformanceOptimizer.endTiming('decodeToken', 'success');
            return decoded;
        } catch (error) {
            console.error('🔐 [Auth] Token decode error:', error);
            authPerformanceOptimizer.endTiming('decodeToken', 'error');
            return null;
        }
    }

    // Optimized login with performance monitoring
    async login(data: LoginData): Promise<LoginResponse> {
        authPerformanceOptimizer.startTiming('login');
        
        try {
            const response = await axios.post(`${AUTH_API_URL}/login`, { ...data, deviceId: getDeviceId() });
            
            // Handle password reset required (status 202)
            if (response.status === 202 && response.data.passwordResetRequired) {
                authPerformanceOptimizer.endTiming('login', 'password-reset-required');
                return response.data;
            }
            
            // Normal login success
            if (response.data.token) {
                // Update cache immediately
                this.tokenCache = response.data.token;
                this.lastCacheTime = Date.now();
                
                localStorage.setItem('token', response.data.token);
                
                // Store user info with caching
                if (response.data.user) {
                    this.userInfoCache = response.data.user;
                    localStorage.setItem('userInfo', JSON.stringify(response.data.user));
                }

                // Invalidate auth state cache to force refresh
                AuthOptimizations.authStateManager.invalidateCache();
            }
            
            authPerformanceOptimizer.endTiming('login', 'success');
            return response.data;
            
        } catch (error: any) {
            console.error('🔐 [Auth] Login error:', error);
            authPerformanceOptimizer.endTiming('login', 'error');
            throw error;
        }
    }

    // Optimized company registration
    async registerCompany(data: RegisterCompanyData): Promise<any> {
        authPerformanceOptimizer.startTiming('registerCompany');
        
        try {
            const response = await axios.post(`${AUTH_API_URL}/register-company`, { ...data, deviceId: getDeviceId() });
            
            if (response.data.token) {
                this.tokenCache = response.data.token;
                this.lastCacheTime = Date.now();
                localStorage.setItem('token', response.data.token);
            }
            
            authPerformanceOptimizer.endTiming('registerCompany', 'success');
            return response.data;
            
        } catch (error) {
            authPerformanceOptimizer.endTiming('registerCompany', 'error');
            throw error;
        }
    }

    // Fast password reset
    async setNewPassword(data: SetNewPasswordData): Promise<LoginResponse> {
        authPerformanceOptimizer.startTiming('setNewPassword');
        
        try {
            const response = await axios.post(`${AUTH_API_URL}/set-new-password`, { ...data, deviceId: getDeviceId() });
            
            if (response.data.token) {
                this.tokenCache = response.data.token;
                this.lastCacheTime = Date.now();
                localStorage.setItem('token', response.data.token);
                
                if (response.data.user) {
                    this.userInfoCache = response.data.user;
                    localStorage.setItem('userInfo', JSON.stringify(response.data.user));
                }
            }
            
            authPerformanceOptimizer.endTiming('setNewPassword', 'success');
            return response.data;
            
        } catch (error) {
            authPerformanceOptimizer.endTiming('setNewPassword', 'error');
            throw error;
        }
    }

    // Fast logout with cache clearing
    logout(): void {
        authPerformanceOptimizer.startTiming('logout');
        
        // Clear all caches
        this.tokenCache = null;
        this.userInfoCache = null;
        this.lastCacheTime = 0;
        
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        
        // Invalidate auth state
        AuthOptimizations.authStateManager.invalidateCache();
        
        authPerformanceOptimizer.endTiming('logout', 'success');
        console.log('🔐 [Auth] User logged out, caches cleared');
    }

    // Fast user info retrieval with caching
    getUserInfo(): any {
        authPerformanceOptimizer.startTiming('getUserInfo');
        
        // Check cache first
        if (this.userInfoCache && Date.now() - this.lastCacheTime < this.CACHE_DURATION) {
            authPerformanceOptimizer.endTiming('getUserInfo', 'cached');
            return this.userInfoCache;
        }

        // Get from localStorage
        try {
            const userInfo = localStorage.getItem('userInfo');
            if (userInfo) {
                this.userInfoCache = JSON.parse(userInfo);
                authPerformanceOptimizer.endTiming('getUserInfo', 'found');
                return this.userInfoCache;
            }
        } catch (error) {
            console.error('🔐 [Auth] Error parsing user info:', error);
        }

        authPerformanceOptimizer.endTiming('getUserInfo', 'not-found');
        return null;
    }

    // Get default chat type from user info
    getDefaultChatType(): 'rag' | 'gpt' {
        const userInfo = this.getUserInfo();
        return userInfo?.defaultChatType || 'gpt';
    }

    // Fetch language setting from database (company settings or system settings)
    // Uses the /language endpoint which is accessible to all authenticated users (including members)
    async fetchLanguageFromDatabase(): Promise<string | null> {
        authPerformanceOptimizer.startTiming('fetchLanguageFromDatabase');
        
        try {
            const token = this.getToken();
            if (!token) {
                console.log('[Auth] No token found, cannot fetch language from database');
                authPerformanceOptimizer.endTiming('fetchLanguageFromDatabase', 'no-token');
                return null;
            }

            // Fetch language setting from server (accessible to all authenticated users)
            const response = await axios.get(`${API_BASE_URL}/api/v1/system-settings/language`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success && response.data.data) {
                const language = response.data.data.defaultLanguage;
                if (language === 'en' || language === 'zh') {
                    console.log('[Auth] Fetched language from database:', language);
                    // Save to localStorage for faster subsequent loads
                    localStorage.setItem('userLanguage', language);
                    authPerformanceOptimizer.endTiming('fetchLanguageFromDatabase', 'success');
                    return language;
                }
            }

            console.log('[Auth] No valid language found in database response');
            authPerformanceOptimizer.endTiming('fetchLanguageFromDatabase', 'not-found');
            return null;
        } catch (error: any) {
            console.error('[Auth] Error fetching language from database:', error);
            if (error.response) {
                console.error('[Auth] Response status:', error.response.status);
                console.error('[Auth] Response data:', error.response.data);
            }
            authPerformanceOptimizer.endTiming('fetchLanguageFromDatabase', 'error');
            return null;
        }
    }

    // Fast authentication state check
    getAuthState() {
        return AuthOptimizations.authStateManager.getAuthState();
    }

    // Get performance report
    getPerformanceReport() {
        return authPerformanceOptimizer.getPerformanceReport();
    }

    // Clear performance logs (useful for testing)
    clearPerformanceLog() {
        authPerformanceOptimizer.clearPerformanceLog();
    }
}

// Create optimized auth service instance
export const optimizedAuthService = new OptimizedAuthService();

// Backward compatibility - export with original interface
export const authService = {
    async registerCompany(data: RegisterCompanyData) {
        return optimizedAuthService.registerCompany(data);
    },

    async login(data: LoginData): Promise<LoginResponse> {
        return optimizedAuthService.login(data);
    },

    async setNewPassword(data: SetNewPasswordData): Promise<LoginResponse> {
        return optimizedAuthService.setNewPassword(data);
    },

    logout(): void {
        optimizedAuthService.logout();
    },

    getToken(): string | null {
        return optimizedAuthService.getToken();
    },

    decodeToken(): DecodedToken | null {
        return optimizedAuthService.decodeToken();
    },

    getUserInfo(): any {
        return optimizedAuthService.getUserInfo();
    },

    isTokenValid(): boolean {
        return optimizedAuthService.isTokenValid();
    },

    getDefaultChatType(): 'rag' | 'gpt' {
        return optimizedAuthService.getDefaultChatType();
    },

    async fetchLanguageFromDatabase(): Promise<string | null> {
        return optimizedAuthService.fetchLanguageFromDatabase();
    },

    getAuthState() {
        return optimizedAuthService.getAuthState();
    },

    // Performance monitoring methods
    getPerformanceReport() {
        return optimizedAuthService.getPerformanceReport();
    },

    clearPerformanceLog() {
        optimizedAuthService.clearPerformanceLog();
    }
};

export default optimizedAuthService;
